import React, { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import { Formik, Form, FormikValues, useFormikContext } from "formik"
import dynamic from "next/dynamic"
import Image from "next/legacy/image"
import Link from "next/link"
import pluralize from "pluralize"
import { OverlayTrigger, Tooltip } from "react-bootstrap"
import * as Yup from "yup"
import { HighlightIcon, HighlightTitle } from "./product-page/highlight"
import S from "./product-page.module.scss"
import CartIcon from "../svgs/cart.svg"
import HeartFilledIcon from "../svgs/heart-filled.svg"
import HeartIcon from "../svgs/heart.svg"
import InfoCircleIcon from "../svgs/info-circle.svg"
import MoodSmile from "../svgs/mood-smile.svg"
import TickIcon from "../svgs/tick.svg"
import DescriptionFabrics, {
  getFabricConfig,
} from "./product-page/description-fabrics"
import Breadcrumbs from "@/components/breadcrumbs"
import ColourVariantSelector from "@/components/colour-variant-selector"
import Layout from "@/components/layout"
import PageHeader from "@/components/page-header"
import QuantityInput from "@/components/quantity-input"
import RadioButtons from "@/components/radio-buttons"
import SectionTitle from "@/components/section-title"
import TextInput from "@/components/text-input"
import API from "@/lib/api"
import {
  formatLength,
  formatQuantityUnit,
  formatUnit,
  getBoxUnit,
  getCutLength,
  getPackSize,
  getProductBase,
  getProductDefaultQuantity,
  getProductDefaultQuantityUnit,
  roundLength,
} from "@/lib/product"
import { event } from "@/lib/tracking"
import useUser from "@/lib/useUser"
import { formatPrice, readableDate } from "@/lib/utils"
import RatingModal from "@/partials/product-page/rating-modal"
import useWishlist from "@/queries/useWishlist"
import { Product, ProductPageProps } from "@/types"

const UNIT_SELECTABLE_CATEGORIES = new Set([
  "components",
  "point of sale",
  "tooling",
])
const HOW_TO_ORDER_CATEGORIES = new Set([
  "components",
  "fabric",
  "point of sale",
  "tooling",
])
const BATCH_QUANTITY_CATEGORIES = new Set(["tooling", "point of sale"])

const RecommendationsSlider = dynamic(
  () => import("./product-page/recommendations-slider"),
  {
    ssr: false,
    loading: () => <div className={S.recommendationsPlaceholder} />,
  },
)

const toOrderedQuantity = (
  product: Product,
  quantity: number,
  unit?: string | null,
) => {
  const cutLength = getCutLength(product, unit)

  return cutLength ? roundLength(quantity * cutLength.value) : quantity
}

const PriceObserver: React.FC<{ product: Product }> = ({ product }) => {
  const { user } = useUser()
  const queryClient = useQueryClient()
  const { values } = useFormikContext<{
    quantity: number
    quantityUnit: string
  }>()

  const queryKey = ["price", product.slug]

  const price = useQuery<{ total: number }>({
    queryKey,
    queryFn: () =>
      API.services.products.getPrice(
        product.slug,
        {
          quantity: toOrderedQuantity(
            product,
            values.quantity,
            values.quantityUnit,
          ),
          unit: values.quantityUnit,
        },
        user?.access_token!,
      ),
    enabled: !!user?.access_token && !!values.quantity,
    retry: false,
  })

  useEffect(() => {
    if (!!user?.access_token && !!values.quantity) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      price.refetch()
    }
  }, [price, user?.access_token, values.quantity, values.quantityUnit])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    queryClient.resetQueries({ queryKey })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.quantityUnit])

  const isInStock = !product.out_of_stock_comments && !product.due_date_type

  if (!price.data || price.data.total === null) return null

  return (
    <div className={S.priceObserver}>
      <p className={S.label}>Price</p>
      <div className={S.priceRow}>
        <p className={S.price}>{formatPrice(price.data.total)}</p>
        {isInStock && <span className={S.inStockBadge}>In stock</span>}
      </div>
      {product.cut_surcharge > 0 && (
        <p className={S.surchargeNote}>
          + {formatPrice(product.cut_surcharge)} cut charge on non-standard
          lengths, added at basket.
        </p>
      )}
    </div>
  )
}

const ProductPage: React.FC<ProductPageProps> = ({
  product,
  recommendations,
  highlights,
}) => {
  const base = getProductBase(product)
  const categoryName = product.main_category.name.toLowerCase()
  const { user } = useUser()
  const queryClient = useQueryClient()
  const [added, setAdded] = useState(false)
  const [modal, setModal] = useState<"RATING">()
  const { data: wishlist } = useWishlist()
  const isDiscontinued = product.out_of_stock_comments === "DISCONTINUED"

  const isFavourited = wishlist?.some((item) => item.product.id === product.id)

  const wishlistToggle = useMutation({
    mutationFn: async () => {
      await API.services.wishlist.toggle(user?.access_token!, {
        product_id: product.id,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["wishlist"] })
    },
  })

  const isFabric = categoryName === "fabric"
  const packSize = getPackSize(product)
  const hasUnitOptions =
    UNIT_SELECTABLE_CATEGORIES.has(categoryName) &&
    Object.keys(product.supported_units).length > 0

  const validationSchema = Yup.object({
    quantityUnit: Yup.string().nullable(),
    quantity: Yup.number()
      .required(
        isFabric ? "Enter the length you need." : "Quantity is required.",
      )
      .when("quantityUnit", (quantityUnitValues, schema) => {
        const [quantityUnit] = quantityUnitValues as [string | null]

        if (getCutLength(product, quantityUnit)) {
          return schema
            .min(1, "Order at least 1 length.")
            .integer("Enter a whole number of lengths.")
        }

        const unitConfig = quantityUnit
          ? product.supported_units[quantityUnit]
          : null
        const min = unitConfig?.min || (product.partially_purchasable ? 1.0 : 1)
        const step =
          unitConfig?.step || (product.partially_purchasable ? 0.1 : 1)
        const minMessage = isFabric
          ? `Minimum cut length is ${min}m.`
          : `Value must be greater than or equal to ${min}.`
        const stepMessage = packSize
          ? `Enter a multiple of ${packSize} (1 pack = ${packSize}).`
          : `Value must be a multiple of ${step}.`

        return schema
          .min(min, minMessage)
          .test("step", stepMessage, (value) => {
            if (value == null) return true
            const remainder = Math.round(((value - min) % step) * 1e10) / 1e10
            return remainder === 0 || remainder === step
          })
      })
      .typeError(
        isFabric
          ? "Enter a length in metres, for example 5.5."
          : "Please enter a valid number.",
      ),
  })

  const {
    rollLength = 20,
    minCutLength = 1,
    maxCutLength = 18,
  } = getFabricConfig(product) || {}

  const renderQuantityField = (values: {
    quantity: number | null
    quantityUnit?: string
  }) => {
    const cutLength = getCutLength(product, values.quantityUnit)

    if (cutLength) {
      const lengths = Number(values.quantity)

      return (
        <>
          <TextInput
            label="Quantity"
            name="quantity"
            type="number"
            suffix={pluralize("length", lengths)}
            placeholder="e.g. 1"
            inputMode="numeric"
            step={1}
            min={1}
            required
          />
          {lengths > 0 && (
            <p className={S.derivedTotal}>
              Total length:{" "}
              <b>{formatLength(lengths * cutLength.value, cutLength.suffix)}</b>{" "}
              ({lengths} x {formatUnit(product.cut_length_unit)})
            </p>
          )}
        </>
      )
    }

    if (!values.quantityUnit && !product.cut_length_unit.includes("m")) {
      return (
        <QuantityInput
          label="Quantity"
          name="quantity"
          partial={product.partially_purchasable}
        />
      )
    }

    const isBox = values.quantityUnit === "Box"
    const boxUnit = getBoxUnit(product)
    const boxes = Number(values.quantity)
    const step =
      product.supported_units[values.quantityUnit!]?.step ||
      (product.partially_purchasable ? 0.1 : 1)
    const min =
      product.supported_units[values.quantityUnit!]?.min ||
      (product.partially_purchasable ? 1.0 : 1)

    const helpText = () => {
      if (isBox) {
        return undefined
      }

      if (isFabric) {
        return `Enter the total metres required. 1 roll = ${rollLength}m. Cut lengths are available from ${minCutLength}m to ${maxCutLength}m.`
      }

      return packSize && values.quantityUnit
        ? `1 pack = ${packSize}`
        : undefined
    }

    return (
      <>
        <TextInput
          label={isFabric ? "Length required" : "Quantity"}
          name="quantity"
          type="number"
          suffix={formatQuantityUnit(values.quantityUnit, boxes) ?? "m"}
          placeholder={isFabric ? "e.g. 5.5" : `e.g. ${min}`}
          step={step}
          min={min}
          required
          helpText={helpText()}
        />
        {isBox && boxUnit && boxes > 0 && (
          <p className={S.derivedTotal}>
            {boxUnit.isLength ? "Total length" : "Total quantity"}:{" "}
            <b>{boxUnit.format(boxes * boxUnit.value)}</b> ({boxes} x{" "}
            {boxUnit.format(boxUnit.value)})
          </p>
        )}
      </>
    )
  }

  useEffect(() => {
    if (added) {
      setTimeout(() => {
        setAdded(false)
      }, 2000)
    }
  }, [added])

  const cart = useMutation({
    mutationFn: async (values: FormikValues) => {
      await API.services.cart.add(user?.access_token!, {
        product_id: product.id,
        quantity: toOrderedQuantity(
          product,
          values.quantity,
          values.quantityUnit,
        ),
        quantity_unit: values.quantityUnit,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["cart"] })
      setAdded(true)
      event({
        category: "ecommerce",
        name: "add-to-cart",
        action: "add",
        customFields: {
          product,
        },
      })
    },
  })

  return (
    <Layout title={product.product_title}>
      <PageHeader
        title={product.main_category.name}
        scrollButton={false}
        as="p"
      />
      <Breadcrumbs
        current={[
          {
            url: `/${base.path}`,
            title: base.name,
          },
          {
            title: product.product_title,
          },
        ]}
      />
      <section className="bg-gray u-section-padding-md">
        <div className="container">
          <p className={S.categoryTitle}>{product.main_category.name}</p>
          <h1 className={S.title}>{product.product_title}</h1>
          <p className={S.productMeta}>
            Product code: {product.item_number}
            {product.fabric_type && (
              <>
                <span className={S.productMetaSeparator}>|</span>
                Fabric type: {product.fabric_type.name}
              </>
            )}
          </p>

          <div className="row">
            <div className="col-md">
              <div className={S.imageWrapper}>
                {user?.isLoggedIn && (
                  <button
                    type="button"
                    className={S.rateButton}
                    onClick={() => setModal("RATING")}
                  >
                    <MoodSmile /> Rate the Product
                  </button>
                )}
                <Image
                  src={
                    product.image_url || "/images/placeholder-image-product.svg"
                  }
                  alt={product.product_title}
                  width={500}
                  height={500}
                  className="pr-lg-5"
                  quality={100}
                />
              </div>
            </div>
            <div className="col-md mt-4 mt-md-0">
              <div className="my-4">
                <p className={S.highlightTitle}>Description</p>
                <div className="wysiwyg mt-1">{product.description}</div>
              </div>
              {HOW_TO_ORDER_CATEGORIES.has(categoryName) && (
                <>
                  <hr />
                  <div className="my-4">
                    <p className={S.highlightTitle}>How to order</p>
                    <div className="wysiwyg mt-1">
                      {categoryName === "fabric" && (
                        <DescriptionFabrics product={product} />
                      )}
                      {categoryName === "components" && !hasUnitOptions && (
                        <p>Select the quantity required.</p>
                      )}
                      {categoryName === "components" &&
                        hasUnitOptions &&
                        !product.cut_length_warehouse && (
                          <p>Enter the number of full boxes required.</p>
                        )}
                      {categoryName === "components" &&
                        hasUnitOptions &&
                        product.cut_length_warehouse && (
                          <>
                            <p>Select ‘Box’ to order full boxes.</p>

                            <p>
                              Select ‘Cut Length’ to order individual Cut Length
                              Units. Enter the number required and the total
                              length will be shown below.
                            </p>

                            <p>
                              To order full boxes with additional Cut Length
                              Units, either add the boxes and additional units
                              separately, or enter the total number required
                              under ‘Cut Length’.
                            </p>
                          </>
                        )}
                      {BATCH_QUANTITY_CATEGORIES.has(categoryName) && (
                        <>
                          <p className="font-weight-bold">
                            PLEASE NOTE THE CHANGE IN HOW YOU NOW ORDER.
                          </p>

                          <p>
                            If batch quantity is each:
                            <br />
                            State quantity of 1 if you require 1 item
                          </p>

                          <p>
                            If batch quantity is 10:
                            <br />
                            State quantity as 1 for a pack of 10
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}

              <div className="divider" />

              {hasUnitOptions && (
                <div className={S.orderTypeSection}>
                  <p>Order type</p>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="order-type-tooltip">
                        Please select whether you would like to order by cut
                        length or full boxes
                      </Tooltip>
                    }
                  >
                    <InfoCircleIcon className={S.tooltipIcon} />
                  </OverlayTrigger>
                </div>
              )}
              {highlights && highlights.length > 0 && (
                <div className={S.highlights}>
                  <div className="row">
                    {highlights.map((item) => (
                      <div className="col-md" key={item.type}>
                        <div className="d-flex align-items-center">
                          <div
                            className={clsx(
                              S.highlightIcon,
                              "d-none d-md-block",
                            )}
                          >
                            <HighlightIcon type={item.type} />
                          </div>
                          <div className="d-flex d-md-block align-items-center justify-content-between w-100 py-1 py-md-0">
                            <HighlightTitle
                              className={S.highlightTitle}
                              title={item.title}
                              type={item.type}
                            />
                            <p className={S.highlightContent}>{item.content}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Formik
                initialValues={{
                  quantityUnit: getProductDefaultQuantityUnit(product),
                  quantity: getProductDefaultQuantity(product) || null,
                }}
                validationSchema={validationSchema}
                onSubmit={(values) => cart.mutateAsync(values)}
              >
                {({ isSubmitting, values, setFieldValue }) => (
                  <Form noValidate className={S.attributes}>
                    {hasUnitOptions && (
                      <div>
                        <RadioButtons
                          name="quantityUnit"
                          options={[
                            ...(product.cut_length_warehouse
                              ? [
                                  {
                                    label: "Cut Length",
                                    value: product.stock_unit.replaceAll(
                                      /\d/g,
                                      "",
                                    ),
                                  },
                                ]
                              : []),
                            {
                              label: "Box",
                              value: "Box",
                            },
                          ]}
                          onChange={async () => {
                            await setFieldValue("quantity", "")
                          }}
                        />
                      </div>
                    )}
                    <div className="row">
                      <div className="col-md">
                        {renderQuantityField(values)}
                      </div>
                      {product.colour_variations && (
                        <div className="col-md mt-3 mt-md-0">
                          <ColourVariantSelector product={product} />
                        </div>
                      )}
                    </div>
                    {isDiscontinued && (
                      <>
                        <hr />
                        <div className={S.outOfStock}>
                          <p className={clsx(S.badge, S.discontinued)}>
                            Discontinued
                          </p>
                        </div>
                      </>
                    )}
                    {(product.due_date_type || product.out_of_stock_comments) &&
                      !isDiscontinued && (
                        <>
                          <hr />
                          <div className={S.outOfStock}>
                            <p className={S.badge}>Out of stock</p>
                            <p className={S.comment}>
                              {product.due_date_type && (
                                <>{product.due_date_type}: </>
                              )}
                              {product.due_date && (
                                <b>{readableDate(product.due_date)}</b>
                              )}
                            </p>
                            {product.out_of_stock_comments && (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id="out-of-stock-comment">
                                    {product.out_of_stock_comments}
                                  </Tooltip>
                                }
                              >
                                <InfoCircleIcon className={S.tooltipIcon} />
                              </OverlayTrigger>
                            )}
                          </div>
                        </>
                      )}
                    <PriceObserver product={product} />
                    {!isDiscontinued && (
                      <div className={S.actions}>
                        {user && user.isLoggedIn ? (
                          <>
                            <button
                              type="button"
                              className={clsx(S.favouriteButton, {
                                [S.favourited]: isFavourited,
                              })}
                              onClick={() => wishlistToggle.mutate()}
                              disabled={wishlistToggle.isPending}
                              aria-label={
                                isFavourited
                                  ? "Remove from favourites"
                                  : "Add to favourites"
                              }
                            >
                              {isFavourited ? (
                                <HeartFilledIcon />
                              ) : (
                                <HeartIcon />
                              )}
                            </button>
                            <button
                              type="submit"
                              className={clsx("btn btn-primary btn-block", {
                                loading: isSubmitting,
                                "btn-success": added,
                              })}
                              disabled={isSubmitting || added}
                            >
                              {added ? (
                                <>
                                  <TickIcon />
                                  Added to basket
                                </>
                              ) : (
                                <>
                                  <CartIcon />
                                  Add to basket
                                </>
                              )}
                            </button>
                          </>
                        ) : (
                          <Link
                            href="/login"
                            className="btn btn-primary btn-block"
                          >
                            Log in to add to basket
                          </Link>
                        )}
                      </div>
                    )}
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </section>

      {recommendations && recommendations.length > 0 ? (
        <section className="bg-white u-section-padding-md">
          <div className="container">
            <SectionTitle
              theme="white"
              className="text-left mt-md-5 mb-4 mb-md-5"
            >
              Our Recommendations
            </SectionTitle>

            <RecommendationsSlider recommendations={recommendations} />
          </div>
        </section>
      ) : null}

      <RatingModal
        open={modal === "RATING"}
        product={product}
        onClose={() => setModal(undefined)}
      />
    </Layout>
  )
}

export default ProductPage
